// ================================================
// EXECUTIVE TIME AUDIT PANEL
// Feature 7 - Weekly time visualization and effectiveness
// ================================================
import React, { useState } from 'react';
import { 
  Clock, ChevronLeft, ChevronRight, Download, 
  TrendingUp, TrendingDown, AlertTriangle, Target,
  Lightbulb, BarChart3
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS } from '../focus';
import { timeAuditData } from '../../data/chiefOfStaffData';

const TimeAuditPanel = () => {
  const [selectedWeek, setSelectedWeek] = useState(2); // Current week index
  const weekOptions = ['Feb 3-9', 'Feb 10-16', 'Feb 17-23'];

  const data = timeAuditData;

  const laneColors = {
    vision: '#8B5CF6',
    strategy: '#3B82F6',
    relationships: '#F472B6',
    operations: '#10B981',
    execution: '#06B6D4',
    creative: '#EC4899',
    marketing: '#F59E0B',
    culture: '#EF4444'
  };

  // Calculate totals
  const plannedTotal = Object.values(data.currentWeek.planned).reduce((a, b) => a + b, 0);
  const actualTotal = Object.values(data.currentWeek.actual).reduce((a, b) => a + b, 0);

  // Calculate lane alignment issues
  const laneIssues = Object.entries(data.currentWeek.byLane)
    .filter(([lane, values]) => {
      const diff = values.actual - values.planned;
      return Math.abs(diff) > 2; // More than 2 hour difference
    })
    .map(([lane, values]) => ({
      lane,
      planned: values.planned,
      actual: values.actual,
      diff: values.actual - values.planned,
      isOverspent: values.actual > values.planned
    }));

  const handleExport = () => {
    alert('This would generate a formatted time audit report for the weekly executive briefing');
  };

  return (
    <div className="p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
          <div>
            <h3 className="text-sm font-bold text-white">Executive Time Audit</h3>
            <p className="text-xs text-gray-400">Week of {weekOptions[selectedWeek]}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSelectedWeek(Math.max(0, selectedWeek - 1))}
            disabled={selectedWeek === 0}
            className="p-1.5 rounded-lg bg-white/10 text-white disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setSelectedWeek(Math.min(weekOptions.length - 1, selectedWeek + 1))}
            disabled={selectedWeek === weekOptions.length - 1}
            className="p-1.5 rounded-lg bg-white/10 text-white disabled:opacity-30"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 text-white"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Executive Effectiveness Score */}
      <div 
        className="p-5 rounded-xl text-center"
        style={{ 
          backgroundColor: '#161B22',
          border: `1px solid ${data.executiveEffectivenessScore < 70 ? '#F59E0B30' : CHIEF_OF_STAFF_COLORS.primary + '30'}`
        }}
      >
        <p className="text-xs text-gray-400 mb-2">Executive Effectiveness Score</p>
        <div className="flex items-center justify-center gap-4">
          <div className="relative w-24 h-24">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="48" cy="48" r="40" fill="none" stroke="#0D1117" strokeWidth="8" />
              <circle 
                cx="48" cy="48" r="40" fill="none" 
                stroke={data.executiveEffectivenessScore >= 70 ? '#10B981' : data.executiveEffectivenessScore >= 50 ? '#F59E0B' : '#EF4444'}
                strokeWidth="8"
                strokeDasharray={`${(data.executiveEffectivenessScore / 100) * 251} 251`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span 
                className="text-3xl font-bold"
                style={{ color: data.executiveEffectivenessScore >= 70 ? '#10B981' : data.executiveEffectivenessScore >= 50 ? '#F59E0B' : '#EF4444' }}
              >
                {data.executiveEffectivenessScore}
              </span>
            </div>
          </div>
          <div className="text-left">
            <p className="text-sm text-white font-medium">
              {data.executiveEffectivenessScore >= 70 ? 'Good Alignment' : data.executiveEffectivenessScore >= 50 ? 'Needs Attention' : 'Critical Misalignment'}
            </p>
            <p className="text-xs text-gray-400">
              Based on lane alignment, decision quality, and meeting efficiency
            </p>
          </div>
        </div>
      </div>

      {/* Planned vs Actual */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
          <BarChart3 className="w-3 h-3" />
          Planned vs Actual Hours
        </h4>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#0D1117' }}>
            <p className="text-2xl font-bold" style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>
              {plannedTotal}h
            </p>
            <p className="text-xs text-gray-400">Planned</p>
          </div>
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#0D1117' }}>
            <p className="text-2xl font-bold text-white flex items-center justify-center gap-2">
              {actualTotal}h
              {actualTotal > plannedTotal 
                ? <TrendingUp className="w-4 h-4 text-red-500" />
                : <TrendingDown className="w-4 h-4 text-green-500" />
              }
            </p>
            <p className="text-xs text-gray-400">Actual</p>
          </div>
        </div>
        
        {/* Activity Breakdown */}
        <div className="space-y-2">
          {Object.entries(data.currentWeek.planned).map(([activity, planned]) => {
            const actual = data.currentWeek.actual[activity];
            const diff = actual - planned;
            
            return (
              <div key={activity} className="flex items-center gap-2">
                <span className="text-xs text-white capitalize w-24">{activity}</span>
                <div className="flex-1 flex gap-1">
                  <div 
                    className="h-4 rounded-l flex items-center justify-end pr-1"
                    style={{ 
                      width: `${(planned / Math.max(planned, actual)) * 100}%`,
                      backgroundColor: CHIEF_OF_STAFF_COLORS.primary + '40'
                    }}
                  >
                    <span className="text-[10px] text-gray-300">{planned}h</span>
                  </div>
                  <div 
                    className="h-4 rounded-r flex items-center pl-1"
                    style={{ 
                      width: `${(actual / Math.max(planned, actual)) * 100}%`,
                      backgroundColor: diff > 0 ? '#EF444440' : '#10B98140'
                    }}
                  >
                    <span className="text-[10px] text-gray-300">{actual}h</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cognitive Lane Distribution */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
          <Target className="w-3 h-3" />
          Cognitive Lane Time Distribution
        </h4>
        
        <div className="space-y-2">
          {Object.entries(data.currentWeek.byLane).map(([lane, values]) => {
            const maxHours = Math.max(...Object.values(data.currentWeek.byLane).flatMap(v => [v.planned, v.actual]));
            const issue = laneIssues.find(i => i.lane === lane);
            
            return (
              <div key={lane} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white capitalize flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: laneColors[lane] }} />
                    {lane}
                  </span>
                  {issue && (
                    <span 
                      className="text-xs flex items-center gap-1"
                      style={{ color: issue.isOverspent ? '#EF4444' : '#10B981' }}
                    >
                      {issue.isOverspent ? '+' : ''}{issue.diff}h
                    </span>
                  )}
                </div>
                <div className="flex gap-1 h-3">
                  <div 
                    className="h-full rounded-l"
                    style={{ 
                      width: `${(values.planned / maxHours) * 100}%`,
                      backgroundColor: laneColors[lane] + '40',
                      minWidth: values.planned > 0 ? '20px' : '0'
                    }}
                  />
                  <div 
                    className="h-full rounded-r"
                    style={{ 
                      width: `${(values.actual / maxHours) * 100}%`,
                      backgroundColor: laneColors[lane],
                      minWidth: values.actual > 0 ? '20px' : '0'
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary + '40' }} />
            Planned
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary }} />
            Actual
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
          <Lightbulb className="w-3 h-3" />
          Recommendations
        </h4>
        
        <div className="space-y-3">
          {data.recommendations.map((rec) => (
            <div 
              key={rec.id}
              className="p-3 rounded-lg"
              style={{ backgroundColor: '#0D1117' }}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-white">{rec.text}</p>
                <span 
                  className="text-xs font-bold px-2 py-1 rounded flex-shrink-0"
                  style={{ 
                    backgroundColor: rec.impact === 'high' ? '#10B98120' : '#F59E0B20',
                    color: rec.impact === 'high' ? '#10B981' : '#F59E0B'
                  }}
                >
                  {rec.timeRecovery}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly History Trend */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-4">
          8-Week Effectiveness Trend
        </h4>
        
        <div className="flex items-end gap-2 h-20">
          {data.weeklyHistory.map((week, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
              <div 
                className="w-full rounded-t transition-all"
                style={{ 
                  height: `${(week.effectivenessScore / 100) * 60}px`,
                  backgroundColor: idx === data.weeklyHistory.length - 1 
                    ? CHIEF_OF_STAFF_COLORS.primary 
                    : CHIEF_OF_STAFF_COLORS.primary + '60'
                }}
              />
              <span className="text-[10px] text-gray-500">{week.week.split(' ')[1]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimeAuditPanel;
